import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Ship, Plus, Pencil, Trash2, Landmark, Repeat, LifeBuoy, Wallet, CheckCircle2, Clock, AlertTriangle, Search, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATS = [
  { key: 'taxes', label: 'Tax Reserve', icon: Landmark, tone: 'text-amber-600' },
  { key: 'reinvestment', label: 'Reinvestment', icon: Repeat, tone: 'text-sky-600' },
  { key: 'emergency', label: 'Emergency Savings', icon: LifeBuoy, tone: 'text-emerald-600' },
  { key: 'personal', label: 'Personal Spending', icon: Wallet, tone: 'text-rose-600' },
];

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const emptyPct = { taxes: 50, reinvestment: 30, emergency: 10, personal: 10 };

const DATE_FILTERS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: 'Last 7 Days' },
  { key: 'custom', label: 'Custom' },
  { key: 'all', label: 'All Sold' },
];

const isoDay = (dt) => {
  const d = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 10);
};
const dateInFilter = (dstr, filter, from, to) => {
  if (filter === 'all') return true;
  const d = (dstr || '').slice(0, 10);
  if (!d) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayStr = isoDay(today);
  if (filter === 'today') return d === todayStr;
  if (filter === 'yesterday') { const y = new Date(today); y.setDate(y.getDate() - 1); return d === isoDay(y); }
  if (filter === '7d') { const s = new Date(today); s.setDate(s.getDate() - 6); return d >= isoDay(s) && d <= todayStr; }
  if (filter === 'custom') return (!from || d >= from) && (!to || d <= to);
  return true;
};

const CHICAGO_TZ = 'America/Chicago';
// Full timestamps are stored in UTC; display them in the user's local (Chicago) day.
// Date-only strings (e.g. a phone's sold_date) are shown as-is with no timezone shift.
const fmtDate = (s) => {
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  try { return new Date(s).toLocaleDateString('en-CA', { timeZone: CHICAGO_TZ }); }
  catch (e) { return String(s).slice(0, 10); }
};

export default function Shipments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useState('auto');
  const [eligible, setEligible] = useState([]);
  const [eligibleExp, setEligibleExp] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedExpIds, setSelectedExpIds] = useState(new Set());
  const [preview, setPreview] = useState({ lines: [], total: 0, phones_total: 0, expenses_total: 0, expense_lines: [], issues: [] });
  const [phoneSearch, setPhoneSearch] = useState('');
  const [phoneDateFilter, setPhoneDateFilter] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [expSearch, setExpSearch] = useState('');
  const [expDateFilter, setExpDateFilter] = useState('all');
  const [expCustomFrom, setExpCustomFrom] = useState('');
  const [expCustomTo, setExpCustomTo] = useState('');
  const [form, setForm] = useState({ label: '', net_profit: '', status: 'planned', pct: emptyPct, save_as_default: false });

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/shipments/distributions`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadEligible = useCallback(async (dist) => {
    try {
      const [pr, er] = await Promise.all([
        axios.get(`${API}/shipments/eligible-phones`, { withCredentials: true }),
        axios.get(`${API}/shipments/eligible-expenses`, { withCredentials: true }),
      ]);
      let phones = pr.data?.phones || [];
      let exps = er.data?.expenses || [];
      // When editing an auto distribution, merge its own (possibly locked) rows in.
      if (dist?.lines?.length) {
        const have = new Set(phones.map((p) => p.id));
        dist.lines.forEach((l) => { if (!have.has(l.id)) phones = [...phones, { ...l, assigned_planned: null }]; });
      }
      if (dist?.expense_lines?.length) {
        const have = new Set(exps.map((e) => e.id));
        dist.expense_lines.forEach((l) => { if (!have.has(l.id)) exps = [...exps, { ...l, assigned_planned: null }]; });
      }
      setEligible(phones);
      setEligibleExp(exps);
    } catch (e) {
      setEligible([]); setEligibleExp([]);
    }
  }, []);

  const openAdd = async () => {
    setEditing(null);
    setMode('auto');
    setSelectedIds(new Set());
    setSelectedExpIds(new Set());
    setPhoneSearch(''); setPhoneDateFilter('all'); setCustomFrom(''); setCustomTo('');
    setExpSearch(''); setExpDateFilter('all'); setExpCustomFrom(''); setExpCustomTo('');
    setPreview({ lines: [], total: 0, phones_total: 0, expenses_total: 0, expense_lines: [], issues: [] });
    setForm({ label: '', net_profit: '', status: 'planned', pct: data?.default_percentages || emptyPct, save_as_default: false });
    setDialogOpen(true);
    loadEligible(null);
  };

  const openEdit = async (d) => {
    setEditing(d);
    setMode(d.source === 'manual' ? 'manual' : 'auto');
    setSelectedIds(new Set(d.inventory_ids || []));
    setSelectedExpIds(new Set(d.expense_ids || []));
    setPreview({ lines: d.lines || [], total: d.net_profit, phones_total: d.phones_total || 0, expenses_total: d.expenses_total || 0, expense_lines: d.expense_lines || [], issues: [] });
    setForm({ label: d.label, net_profit: String(d.net_profit), status: d.status, pct: { ...d.percentages }, save_as_default: false });
    setDialogOpen(true);
    if (d.source !== 'manual') loadEligible(d);
  };

  // Live server-verified profit + discrepancy issues whenever selection changes (auto mode).
  useEffect(() => {
    if (!dialogOpen || mode !== 'auto') return;
    const ids = Array.from(selectedIds);
    const eids = Array.from(selectedExpIds);
    if (ids.length === 0 && eids.length === 0) {
      setPreview({ lines: [], total: 0, phones_total: 0, expenses_total: 0, expense_lines: [], issues: [] });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/shipments/preview`,
          { inventory_ids: ids, expense_ids: eids, exclude_dist_id: editing?.id || null }, { withCredentials: true });
        if (!cancelled) setPreview(res.data);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [selectedIds, selectedExpIds, dialogOpen, mode, editing]);

  const pctTotal = useMemo(() => CATS.reduce((s, c) => s + (parseFloat(form.pct[c.key]) || 0), 0), [form.pct]);
  const pctValid = Math.round(pctTotal * 100) / 100 === 100;
  const net = mode === 'auto' ? (preview.total || 0) : (parseFloat(form.net_profit) || 0);

  const filteredPhones = useMemo(() => {
    const q = phoneSearch.trim().toLowerCase();
    return eligible
      .filter((p) => dateInFilter(p.sold_date || p.created_at, phoneDateFilter, customFrom, customTo))
      .filter((p) => !q
        || `${p.brand} ${p.model} ${p.storage}`.toLowerCase().includes(q)
        || (p.imei || '').toLowerCase().includes(q)
        || (p.id || '').toLowerCase().includes(q))
      .sort((a, b) => (b.sold_date || b.created_at || '').localeCompare(a.sold_date || a.created_at || ''));
  }, [eligible, phoneSearch, phoneDateFilter, customFrom, customTo]);

  const filteredExps = useMemo(() => {
    const q = expSearch.trim().toLowerCase();
    return eligibleExp
      .filter((e) => dateInFilter(e.date, expDateFilter, expCustomFrom, expCustomTo))
      .filter((e) => !q || `${e.category} ${e.description}`.toLowerCase().includes(q));
  }, [eligibleExp, expSearch, expDateFilter, expCustomFrom, expCustomTo]);

  const selectedPhones = useMemo(() => eligible.filter((p) => selectedIds.has(p.id)), [eligible, selectedIds]);
  const selectedProfit = useMemo(() => selectedPhones.reduce((s, p) => s + (p.profit || 0), 0), [selectedPhones]);
  const selectAllFilteredPhones = () => setSelectedIds((prev) => {
    const n = new Set(prev); filteredPhones.forEach((p) => n.add(p.id)); return n;
  });

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleExp = (id) => {
    setSelectedExpIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const handleSave = async () => {
    if (!pctValid) { toast.error(`Percentages must total 100% (currently ${pctTotal}%)`); return; }
    if (net <= 0) { toast.error('Net profit must be greater than 0'); return; }
    if (mode === 'auto' && selectedIds.size === 0) { toast.error('Select at least one sold phone'); return; }
    const percentages = CATS.reduce((o, c) => ({ ...o, [c.key]: parseFloat(form.pct[c.key]) || 0 }), {});
    const payload = {
      label: form.label || 'Shipment', status: form.status, percentages,
      save_as_default: form.save_as_default,
    };
    if (mode === 'auto') { payload.source = 'auto'; payload.inventory_ids = Array.from(selectedIds); payload.expense_ids = Array.from(selectedExpIds); }
    else { payload.source = 'manual'; payload.net_profit = net; }
    try {
      if (editing) {
        await axios.put(`${API}/shipments/distributions/${editing.id}`, payload, { withCredentials: true });
        toast.success('Distribution updated');
      } else {
        await axios.post(`${API}/shipments/distributions`, payload, { withCredentials: true });
        toast.success('Shipment profit distributed');
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const toggleStatus = async (d) => {
    try {
      await axios.put(`${API}/shipments/distributions/${d.id}`, { status: d.status === 'completed' ? 'planned' : 'completed' }, { withCredentials: true });
      fetchData();
    } catch (e) { toast.error('Failed to update status'); }
  };

  const handleDelete = async (d) => {
    if (!window.confirm(`Delete the "${d.label}" distribution? This does not touch your sales or profit.`)) return;
    try {
      await axios.delete(`${API}/shipments/distributions/${d.id}`, { withCredentials: true });
      toast.success('Distribution deleted');
      fetchData();
    } catch (e) { toast.error('Failed to delete'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary" /></div>;
  }

  const items = data?.items || [];
  const totals = data?.totals || {};
  const planned = data?.totals_planned || {};
  const completed = data?.totals_completed || {};

  return (
    <div className="space-y-6" data-testid="shipments-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl text-foreground tracking-wide">SHIPMENTS</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {items.length} distribution{items.length === 1 ? '' : 's'} · Total distributed (completed) <span className="font-semibold">{fmtMoney(completed.net_profit)}</span> · Completed tax allocations <span className="font-semibold">{fmtMoney(completed.taxes)}</span>
          </p>
        </div>
        <Button onClick={openAdd} data-testid="add-distribution-btn" className="gap-2">
          <Plus className="h-4 w-4" /> Distribute Profit
        </Button>
      </div>

      <p className="text-xs text-muted-foreground rounded-md bg-muted/50 px-3 py-2" data-testid="reserve-note">
        <strong>Tax Reserve</strong> is money you set aside from profit — it is separate from your <strong>estimated tax liability</strong> on the Expenses page, and a reserve does not guarantee your taxes are covered.
        Distributions are <strong>not</strong> business expenses and never reduce your net profit again.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="cumulative-totals">
        {CATS.map((c) => (
          <Card key={c.key} data-testid={`total-${c.key}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{c.label}</span>
                <c.icon className={`h-4 w-4 ${c.tone}`} />
              </div>
              <div className={`text-2xl font-semibold tabular-nums ${c.tone}`}>{fmtMoney(totals[c.key])}</div>
              <div className="text-[11px] text-muted-foreground mt-1 leading-tight">
                Done {fmtMoney(completed[c.key])} · Planned {fmtMoney(planned[c.key])}
                {c.key === 'taxes' && <span className="block">+ opening {fmtMoney(data?.tax_savings_opening)} = reserve {fmtMoney(data?.tax_reserve_total)}</span>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="overflow-hidden border-2 border-foreground/10">
        <div className="bg-sheet text-white px-5 py-3 font-display tracking-widest text-lg">DISTRIBUTIONS</div>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <p className="text-muted-foreground text-center py-12" data-testid="no-distributions">
              No distributions yet. Click “Distribute Profit” after a shipment is paid.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-sheet-soft hover:bg-sheet-soft">
                    <TableHead className="font-bold text-foreground">Shipment</TableHead>
                    <TableHead className="text-right font-bold text-foreground">Net Profit</TableHead>
                    {CATS.map((c) => (<TableHead key={c.key} className="text-right font-bold text-foreground">{c.label}</TableHead>))}
                    <TableHead className="font-bold text-foreground">Status</TableHead>
                    <TableHead className="text-right font-bold text-foreground">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((d) => (
                    <TableRow key={d.id} data-testid={`dist-row-${d.id}`} className="border-b border-foreground/5">
                      <TableCell className="font-medium">
                        {d.label}
                        <div className="text-[11px] text-muted-foreground">
                          {fmtDate(d.date)} · {d.source === 'auto' ? `${(d.inventory_ids || []).length} phone(s)` : 'manual'}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{fmtMoney(d.net_profit)}</TableCell>
                      {CATS.map((c) => (
                        <TableCell key={c.key} className="text-right tabular-nums">
                          {fmtMoney(d.amounts?.[c.key])}
                          <span className="block text-[10px] text-muted-foreground">{d.percentages?.[c.key]}%</span>
                        </TableCell>
                      ))}
                      <TableCell>
                        <button onClick={() => toggleStatus(d)} data-testid={`toggle-status-${d.id}`} className="inline-flex" title="Click to toggle planned / completed">
                          {d.status === 'completed'
                            ? <Badge className="bg-emerald-600 hover:bg-emerald-600 gap-1"><CheckCircle2 className="h-3 w-3" /> Completed</Badge>
                            : <Badge variant="outline" className="gap-1 text-amber-600 border-amber-500/40"><Clock className="h-3 w-3" /> Planned</Badge>}
                        </button>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(d)} data-testid={`edit-dist-${d.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(d)} data-testid={`delete-dist-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="distribution-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wider flex items-center gap-2">
              <Ship className="h-5 w-5 text-primary" /> {editing ? 'EDIT DISTRIBUTION' : 'DISTRIBUTE SHIPMENT PROFIT'}
            </DialogTitle>
            <DialogDescription>Build a shipment from your sold phones, or enter the net profit manually.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Shipment label</Label>
                <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. June batch #3" data-testid="dist-label-input" />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="dist-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planned">Planned (not transferred yet)</SelectItem>
                    <SelectItem value="completed">Completed (money moved)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Tabs value={mode} onValueChange={setMode}>
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="auto" data-testid="mode-auto">From sold phones</TabsTrigger>
                <TabsTrigger value="manual" data-testid="mode-manual">Enter manually</TabsTrigger>
              </TabsList>
            </Tabs>

            {mode === 'auto' ? (
              <div className="space-y-3">
                {/* Phone filters + actions */}
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label>Select sold phones in this shipment</Label>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={selectAllFilteredPhones} data-testid="select-all-filtered">Select all filtered</Button>
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSelectedIds(new Set())} data-testid="clear-selection">Clear selection</Button>
                  </div>
                </div>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                  <Input value={phoneSearch} onChange={(e) => setPhoneSearch(e.target.value)} placeholder="Search model, IMEI, serial, or inventory ID" className="pl-8 h-9" data-testid="phone-search" />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="phone-date-filters">
                  {DATE_FILTERS.map((f) => (
                    <button key={f.key} type="button" onClick={() => setPhoneDateFilter(f.key)} data-testid={`phone-filter-${f.key}`}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${phoneDateFilter === f.key ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground'}`}>{f.label}</button>
                  ))}
                </div>
                {phoneDateFilter === 'custom' && (
                  <div className="flex items-center gap-2" data-testid="phone-custom-range">
                    <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-8 text-xs" data-testid="phone-custom-from" />
                    <span className="text-xs text-muted-foreground">to</span>
                    <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-8 text-xs" data-testid="phone-custom-to" />
                  </div>
                )}

                {/* Phone list */}
                <div className="border rounded-md max-h-72 overflow-y-auto divide-y" data-testid="eligible-phone-list">
                  {filteredPhones.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-4 text-center">
                      {eligible.length === 0 ? 'No eligible sold phones. Record a sale (actual price + sold date) on Inventory first.' : 'No phones match this filter or search.'}
                    </p>
                  ) : filteredPhones.map((p) => (
                    <label key={p.id} className="flex items-center gap-3 p-2.5 hover:bg-muted/40 cursor-pointer" data-testid={`phone-option-${p.id}`}>
                      <Checkbox checked={selectedIds.has(p.id)} onCheckedChange={() => toggleSelect(p.id)} data-testid={`phone-check-${p.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {p.model} {p.storage}
                          <span className="text-muted-foreground font-normal"> · {(p.sold_date || p.created_at || '').slice(0, 10) || 'no date'}</span>
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          IMEI …{(p.imei || '').slice(-4) || '—'} · Sold {fmtMoney(p.sale_price)} · cost {fmtMoney(p.purchase_price)}
                          {p.assigned_planned && <span className="text-amber-600"> · also in planned “{p.assigned_planned}”</span>}
                        </div>
                      </div>
                      <div className="text-sm font-semibold tabular-nums text-emerald-600">{fmtMoney(p.profit)}</div>
                    </label>
                  ))}
                </div>

                {/* Selected summary — persists across filters */}
                <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2" data-testid="selected-summary">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground" data-testid="selected-count">Selected: {selectedPhones.length} phone(s)</span>
                    <span className="text-sm font-semibold tabular-nums text-emerald-600" data-testid="selected-profit">{fmtMoney(selectedProfit)} profit</span>
                  </div>
                  {selectedPhones.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="selected-chips">
                      {selectedPhones.map((p) => (
                        <span key={p.id} className="inline-flex items-center gap-1 text-[11px] bg-background border rounded-full pl-2 pr-1 py-0.5" data-testid={`selected-chip-${p.id}`}>
                          {p.model} …{(p.imei || '').slice(-4) || '—'}
                          <button type="button" onClick={() => toggleSelect(p.id)} className="hover:text-destructive" data-testid={`remove-chip-${p.id}`}><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {preview.issues?.length > 0 && (
                  <div className="text-xs text-amber-600 flex items-start gap-1.5 bg-amber-500/10 rounded px-2 py-1.5" data-testid="preview-issues">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>{preview.issues.length} row(s) skipped: {preview.issues.map((i) => i.reason).join('; ')}</span>
                  </div>
                )}

                {/* Expense picker with search + date filters */}
                <Label className="pt-1">Attach shipping / selling fees &amp; other costs (optional)</Label>
                <p className="text-[11px] text-muted-foreground -mt-1">
                  Pick the business expenses that belong to this shipment. They stay on the Expenses page (counted once in business profit) and are subtracted here only to size this shipment&apos;s payout.
                </p>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                  <Input value={expSearch} onChange={(e) => setExpSearch(e.target.value)} placeholder="Search expenses by category or description" className="pl-8 h-9" data-testid="expense-search" />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="expense-date-filters">
                  {DATE_FILTERS.map((f) => (
                    <button key={f.key} type="button" onClick={() => setExpDateFilter(f.key)} data-testid={`expense-filter-${f.key}`}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${expDateFilter === f.key ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground'}`}>{f.label}</button>
                  ))}
                </div>
                {expDateFilter === 'custom' && (
                  <div className="flex items-center gap-2" data-testid="expense-custom-range">
                    <Input type="date" value={expCustomFrom} onChange={(e) => setExpCustomFrom(e.target.value)} className="h-8 text-xs" data-testid="expense-custom-from" />
                    <span className="text-xs text-muted-foreground">to</span>
                    <Input type="date" value={expCustomTo} onChange={(e) => setExpCustomTo(e.target.value)} className="h-8 text-xs" data-testid="expense-custom-to" />
                  </div>
                )}
                <div className="border rounded-md max-h-44 overflow-y-auto divide-y" data-testid="eligible-expense-list">
                  {filteredExps.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-4 text-center">
                      {eligibleExp.length === 0 ? 'No business expenses to attach. Add shipping/fees on the Expenses page.' : 'No expenses match this filter or search.'}
                    </p>
                  ) : filteredExps.map((e) => (
                    <label key={e.id} className="flex items-center gap-3 p-2.5 hover:bg-muted/40 cursor-pointer" data-testid={`expense-option-${e.id}`}>
                      <Checkbox checked={selectedExpIds.has(e.id)} onCheckedChange={() => toggleExp(e.id)} data-testid={`expense-check-${e.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{e.category || 'Expense'}{e.description ? ` — ${e.description}` : ''}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {(e.date || '').slice(0, 10)}
                          {e.assigned_planned && <span className="text-amber-600"> · also in planned “{e.assigned_planned}”</span>}
                        </div>
                      </div>
                      <div className="text-sm font-semibold tabular-nums text-rose-600">−{fmtMoney(e.amount)}</div>
                    </label>
                  ))}
                </div>

                <div className="rounded-md bg-muted/50 px-3 py-2 space-y-1" data-testid="net-breakdown">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>Phones profit ({preview.lines?.length || 0})</span>
                    <span className="tabular-nums text-emerald-600">{fmtMoney(preview.phones_total)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>− Shipping / selling fees ({preview.expense_lines?.length || 0})</span>
                    <span className="tabular-nums text-rose-600">−{fmtMoney(preview.expenses_total)}</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-foreground/10 pt-1">
                    <span className="text-sm font-medium">Shipment net profit</span>
                    <span className="text-lg font-semibold tabular-nums text-emerald-600" data-testid="auto-net-profit">{fmtMoney(net)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Confirmed net profit ($)</Label>
                <Input type="number" min="0" step="0.01" value={form.net_profit} onChange={(e) => setForm({ ...form, net_profit: e.target.value })} placeholder="0.00" data-testid="dist-net-profit-input" />
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Split percentages</Label>
                <span className={`text-sm font-semibold tabular-nums ${pctValid ? 'text-emerald-600' : 'text-destructive'}`} data-testid="pct-total">Total: {Math.round(pctTotal * 100) / 100}%</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {CATS.map((c) => (
                  <div key={c.key} className="space-y-1">
                    <Label className="text-xs flex items-center gap-1.5 text-muted-foreground"><c.icon className={`h-3.5 w-3.5 ${c.tone}`} /> {c.label}</Label>
                    <div className="flex items-center gap-2">
                      <Input type="number" min="0" max="100" step="0.1" value={form.pct[c.key]} onChange={(e) => setForm({ ...form, pct: { ...form.pct, [c.key]: e.target.value } })} data-testid={`dist-pct-${c.key}`} />
                      <span className="text-sm text-muted-foreground w-20 text-right tabular-nums">{fmtMoney((net * (parseFloat(form.pct[c.key]) || 0)) / 100)}</span>
                    </div>
                  </div>
                ))}
              </div>
              {!pctValid && <p className="text-xs text-destructive" data-testid="pct-error">Percentages must total exactly 100%.</p>}
              {!editing && (
                <label className="flex items-center gap-2 text-sm text-muted-foreground pt-1 cursor-pointer">
                  <Checkbox checked={form.save_as_default} onCheckedChange={(v) => setForm({ ...form, save_as_default: !!v })} data-testid="dist-save-default" />
                  Save this split as my default
                </label>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!pctValid} data-testid="save-distribution-btn">{editing ? 'Save Changes' : 'Distribute'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
